import { Link } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';

export default function HausbauInSuedtirol() {
  return (
    <div className="section-spacing" data-testid="hausbau-suedtirol-page">
      <div className="container-wide">
        {/* Breadcrumb */}
        <nav className="text-sm text-[#64748B] mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#0284C7]">Start</Link>
          <span className="mx-2">/</span>
          <Link to="/ratgeber" className="hover:text-[#0284C7]">Ratgeber</Link>
          <span className="mx-2">/</span>
          <span className="text-[#0F172A]">Hausbau in Südtirol</span>
        </nav>

        {/* Hero */}
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6 font-[var(--font-heading)]">
            Hausbau in Südtirol – worauf achten?
          </h1>
          <p className="text-lg text-[#475569] max-w-3xl">
            Ein Haus in Südtirol zu bauen ist ein besonderes Projekt. Die Kombination 
            aus alpiner Lage, besonderen Bauvorschriften und hohen Qualitätsansprüchen 
            macht es anspruchsvoll – aber auch lohnend.
          </p>
        </div>

        {/* Hero Image */}
        <div className="mb-12 rounded-xl overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1768464395439-25b2ef151f80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxpdGFsaWFuJTIwZG9sb21pdGVzJTIwbW91bnRhaW4lMjB2aWxsYWdlJTIwc2NlbmljfGVufDB8fHx8MTc3NDI1Nzg4MHww&ixlib=rb-4.1.0&q=85"
            alt="Dorf in Südtirol mit Dolomiten"
            className="w-full h-64 md:h-96 object-cover"
          />
        </div>

        {/* Content */}
        <div className="advisor-content">
          <h2>Die größten Herausforderungen</h2>
          
          <h3>1. Grundstücksuche</h3>
          <p>
            Bauland ist in Südtirol knapp und teuer. Die topografischen Gegebenheiten 
            und strenge Raumordnungsgesetze begrenzen das Angebot. Rechne damit, dass 
            die Grundstückssuche Zeit braucht – oft Jahre.
          </p>

          <h3>2. Bauvorschriften</h3>
          <p>
            In Südtirol gelten sowohl italienische als auch lokale Bauvorschriften. 
            Besonders wichtig sind:
          </p>
          <ul>
            <li><strong>Klimahaus-Standard:</strong> Mindestens Klimahaus B ist vorgeschrieben, oft wird A oder A Nature angestrebt</li>
            <li><strong>Kubaturregelungen:</strong> Die bebaubare Kubatur ist streng reglementiert</li>
            <li><strong>Gestaltungsvorschriften:</strong> Viele Gemeinden haben Vorgaben zur Dachform, Farben und Materialien</li>
          </ul>

          <h3>3. Kosten</h3>
          <p>
            Die Baukosten in Südtirol liegen über dem italienischen Durchschnitt. 
            Rechne mit:
          </p>
          <ul>
            <li><strong>Grundstück:</strong> 200-800 €/m² je nach Lage</li>
            <li><strong>Baukosten:</strong> 2.000-3.500 €/m² Wohnfläche</li>
            <li><strong>Nebenkosten:</strong> ca. 15-20% des Kaufpreises/Baukosten</li>
          </ul>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 my-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <strong className="text-yellow-800">Wichtig:</strong>
                <p className="text-yellow-700 text-sm mt-1">
                  Plane immer einen Puffer von 15-20% für Unvorhergesehenes ein. 
                  Besonders bei der Baugrube und den Fundamenten können in alpinen 
                  Lagen Überraschungen auftreten.
                </p>
              </div>
            </div>
          </div>

          <h2>Checkliste: Vor dem Baustart</h2>
          <ul className="space-y-3 my-6">
            {[
              'Grundstück gesichert (Vorvertrag oder Kauf)',
              'Finanzierung geklärt (Bank, Förderungen)',
              'Architekt/Planer beauftragt',
              'Baugenehmigung erteilt',
              'Baufirma ausgewählt (Angebote verglichen)',
              'Bauzeitenplan erstellt',
              'Versicherungen abgeschlossen (Bauherren-Haftpflicht, Rohbau)',
              'Baubegleitung/Bauleitung organisiert'
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#475569]">{item}</span>
              </li>
            ))}
          </ul>

          <h2>Den richtigen Partner finden</h2>
          <p>
            Die Wahl der Baufirma ist entscheidend. Achte auf regionale Erfahrung, 
            Referenzen und ein gutes Bauchgefühl. Eine bewährte Adresse für Bauvorhaben 
            in Südtirol ist{' '}
            <a href="https://hafner-ec.com" target="_blank" rel="noopener noreferrer">
              hafner-ec.com
            </a>{' '}
            – ein Unternehmen mit langjähriger Erfahrung in der Region.
          </p>

          <h2>Förderungen nutzen</h2>
          <p>
            Italien und Südtirol bieten verschiedene Förderungen für energieeffizientes 
            Bauen. Informiere dich frühzeitig über:
          </p>
          <ul>
            <li>Landesbeiträge für Wohnbau</li>
            <li>Steuerliche Abzugsmöglichkeiten</li>
            <li>Förderungen für erneuerbare Energien</li>
          </ul>

          <div className="cta-box">
            <div className="cta-box-title">Neubau oder doch Sanierung?</div>
            <p className="cta-box-text">
              Vielleicht ist für deine Situation eine Sanierung die bessere Option? 
              Unser Entscheidungshelfer gibt dir Orientierung.
            </p>
            <Link to="/tippspiele/neubau-oder-sanierung" className="text-[#0284C7] font-medium hover:underline">
              Zum Entscheidungshelfer: Neubau oder Sanierung? →
            </Link>
          </div>

          {/* Internal Links */}
          <div className="internal-links">
            <div className="internal-links-title">Weiterlesen:</div>
            <div className="internal-links-list">
              <Link to="/haus-und-bau/baufirma-meran" className="internal-link">
                Baufirma in Meran finden
              </Link>
              <Link to="/haus-und-bau/was-kostet-eine-sanierung" className="internal-link">
                Was kostet eine Sanierung?
              </Link>
              <Link to="/tippspiele/was-kostet-dieses-haus" className="internal-link">
                Was kostet dieses Haus?
              </Link>
              <Link to="/suedtirol/wohnen-in-suedtirol" className="internal-link">
                Wohnen in Südtirol
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
